import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Close as CloseIcon, HelpOutlineOutlined as HelpOutlineOutlinedIcon } from '@mui/icons-material';
import { Workflow } from '../../types/Workflow';
import Column from '../../components/Column';
import Row from '../../components/Row';
import ApiService from '../../ApiService';
import { Campaign } from '../../types/Campaign';

export interface CRMDataUploadSendToAmcDialogProps {
    open: boolean;
    dealerId: number;
    onClose(workflowId: number | null, campaignIds: number[]): void;
    onCancel(): void;
}

export default function CRMDataUploadSendToAmcDialog(props: CRMDataUploadSendToAmcDialogProps) {
    const [workflows, setWorkflows] = useState<Workflow[]>([]);
    const [workflowId, setWorkflowId] = useState<number | null>(null);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);

    useEffect(() => {
        getWorkflows();
        getCampaigns(props.dealerId);
    }, [props.dealerId]);

    function getCampaigns(dealerId: number) {
        ApiService.getCampaignsForDealer(dealerId)
            .then((response) => {
                setCampaigns(response.data);
            })
            .catch(() => {
                setCampaigns([]);
            });
    }

    function getWorkflows() {
        ApiService.getWorkflows()
            .then((response) => {
                setWorkflows(response.data);
            })
            .catch(() => {
                setWorkflows([]);
            });
    }

    function cancel() {
        props.onCancel();
    }

    function sendToAmc() {
        const campaignIds = campaigns
            .filter(
                (campaign) =>
                    campaign.deliveryStatus.dspStatusId === 'ENDING' ||
                    campaign.deliveryStatus.dspStatusId === 'DELIVERING'
            )
            .map((campaign) => campaign.id)
            .filter((id): id is number => id !== undefined);

        props.onClose(workflowId, campaignIds);
    }

    function selectWorkflow(workflowId: number) {
        setWorkflowId(workflowId);
    }

    return (
        <Dialog
            fullWidth={true}
            open={props.open}
            onClose={cancel}
            PaperProps={{
                sx: {
                    maxWidth: '1048px',
                },
            }}
        >
            <DialogTitle>Send to AMC</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={cancel}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers sx={{ pt: 3, pb: 5 }}>
                <DialogContentText>
                    <Column spacing={3}>
                        <Column gap={2}>
                            <InputLabel
                                required={true}
                                sx={{
                                    color: (theme) => theme.palette.text.primary,
                                    fontSize: '14px',
                                }}
                            >
                                Select an AMC workflow for this file.
                            </InputLabel>

                            <FormControl style={{ width: '400px' }}>
                                <Row gap={0.75} alignItems="center">
                                    <TextField
                                        label="AMC Workflow Type"
                                        select
                                        required={true}
                                        size="small"
                                        variant="outlined"
                                        onChange={(event) => {
                                            selectWorkflow(Number(event.target.value));
                                        }}
                                        sx={{ width: '100%' }}
                                        slotProps={{
                                            select: { native: false },
                                        }}
                                    >
                                        {workflows.map((workflow) => {
                                            return (
                                                <MenuItem key={workflow.id} value={workflow.id}>
                                                    {workflow.workflowId}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>

                                    <Tooltip
                                        arrow
                                        title="An AMC Workflow is a query we run to match Amazon users to your CRM data."
                                        placement="right"
                                    >
                                        <HelpOutlineOutlinedIcon fontSize="medium" color="action" />
                                    </Tooltip>
                                </Row>
                            </FormControl>
                        </Column>
                    </Column>
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1 }}>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" color="primary" onClick={cancel}>
                        Cancel
                    </Button>
                    <Button color="primary" variant="contained" disabled={!workflowId} onClick={sendToAmc}>
                        Send to AMC
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
